import { useCallback, useEffect, useState } from 'react';

import Head from 'next/head';

import Form from 'epn-ui/umd/dist/components/Form';
import Grid from 'epn-ui/umd/dist/components/Grid';
import Loader from 'epn-ui/umd/dist/components/Loader';
import notification from 'epn-ui/umd/dist/components/Notification';

import FormMarking from '@components/pages/tools/marking/form';
import useI18n from '@i18n';

import api from '@api';
import apiHooks, { ICreateUseRequestInfiniteResponse } from '@apiHooks';
import BaseCopyableLink from '@components/base/BaseCopyableLink';
import modifyUrl from '@utils/urls/modifyURL';

// TYPES
import type {
    IGetCreativesData,
    IGetDomainCuttersData,
    IGetLinkReductionData,
    IGetOfferDetailDataFormatted,
    IGetOffersDataFormatted,
    IGetPlacementsDataFormatted,
    IResponse,
} from '@api/types';
import { isGlobalDomain } from '@config/globalDomain';
import { IS_ALI_LINK } from '@utils/regex';
import type { ICreativeState, IForm } from './interfaces';

// HELPERS
import { prepareCreativeData, prepareFormFields } from './helpers';

// Config
// eslint-disable-next-line import/order
import {
    INITIAL_CREATIVE,
    OFFERS_PARAMS,
    PLACEMENTS_PARAMS,
} from '@components/pages/tools/marking/config';

const { useOffers, usePlacements, useOfferDetail } = apiHooks;

const MarkingPageForm: React.FC = () => {
    const { t, locale } = useI18n('Marking');
    const { t: tErrors } = useI18n('Errors');
    const { t: tTools } = useI18n('Tools');
    const [form] = Form.useForm<IForm>();
    const [offerId, setOfferId] = useState<number | null>(null);
    const [reductionListLoading, setReductionListLoading] = useState<boolean>(true);
    const [reductionListError, setReductionListError] = useState<string | unknown>();
    const [reductionList, setReductionList] = useState<
        {
            label: string;
            value: string;
        }[]
    >(null);
    const [reductionType, setReductionType] = useState<string | null>(null);
    const [reductionsNeed, setReductionsNeed] = useState<boolean>(false);
    const [creativeIsLoading, setCreativeIsLoading] = useState<boolean>(false);
    const [creative, setCreative] = useState<ICreativeState>(INITIAL_CREATIVE);
    const [formError, setFormError] = useState(null);
    const linkForm = Form.useWatch('link', form);
    const [requiredPlacement, setRequiredPlacement] = useState(true);

    const checkAliRuLink = () => {
        const regex = IS_ALI_LINK;
        return regex.test(linkForm);
    };

    const isAli = checkAliRuLink();

    // Get Reductions
    const getReductions = async (): Promise<void> => {
        try {
            const res: IResponse<IGetDomainCuttersData> = await api.linkReduction.getDomainCutterList(
                {},
            );
            if (res.result && res.data?.attributes) {
                const preparedReduction = res.data?.attributes.map(item => ({
                    value: item,
                    label: item,
                }));
                setReductionList(preparedReduction);
                setReductionType(preparedReduction[0].value);
                setReductionListLoading(false);
            }
        } catch (err: unknown) {
            setReductionListError(err);
            console.error(err);
            notification.error({ message: tErrors('Something went wrong') });
        }
    };

    useEffect(() => {
        getReductions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Get offers
    const onGetOffers = data => {
        setOfferId(data[0][0]?.id);
    };
    const { data: offersData, isLoading: offersIsLoading, error: offersError } = useOffers<
        IGetOffersDataFormatted[]
    >({
        params: {
            ...OFFERS_PARAMS,
            lang: locale,
        },
        dependencies: [locale],
        options: { onSuccess: onGetOffers },
    });

    const onGetPlacements = ([data]): void => {
        if (!data?.length) {
            form.resetFields(['placementId']);
            return;
        }

        form.setFieldValue('placementId', data?.[0]?.id?.toString());
    };

    // Get Placements
    const {
        data: placementsData = null,
        isLoading: placementsIsLoading,
        error: placementsError,
    } = usePlacements<IGetPlacementsDataFormatted[]>({
        params: { ...PLACEMENTS_PARAMS, offerIds: offerId },
        dependencies: [offerId],
        condition: !!offerId,
        options: { onSuccess: onGetPlacements },
    });

    const { data: offerDetail } = useOfferDetail<IGetOfferDetailDataFormatted>({
        urlParams: {
            id: offerId,
        },
        params: {
            lang: locale,
            viewRules: 'role_user',
            fields: 'creative_placement',
        },
        dependencies: [offerId],
        condition: !!offerId,
    });

    useEffect((): void => {
        setRequiredPlacement(offerDetail?.creativePlacement);
    }, [offerDetail]);

    // Отображение ошибок
    useEffect(() => {
        if (offersError || placementsError || reductionListError) {
            let msg;
            if (offersError) {
                msg = t('An error occurred while fetching the list of offers');
            }
            if (placementsError) {
                msg = t('An error occurred while fetching the list of placements');
            }
            if (reductionListError) {
                msg = t('An error occurred while fetching the list of link shorteners');
            }
            notification.error({ message: t(msg) });
            console.error(offersError || placementsError || reductionListError);
        }
    }, [offersError, placementsError, reductionListError, t]);

    const resetForm = useCallback((): void => {
        setFormError(null);
        onGetOffers([offersData]);
    }, [offersData]);

    const getSubsArray = (subs: {
        [p: string]: string;
    }): {
        key: string;
        value: string;
    }[] => {
        try {
            const subList = Object.keys(subs);
            if (subList.length === 0) return [];

            const result = subList.reduce((acc, key) => {
                if (subs[key]) {
                    acc.push({
                        key,
                        value: subs[key],
                    });
                }
                return acc;
            }, []);

            return result;
        } catch (err) {
            console.error(err);
            return [];
        }
    };

    const addSubs = (
        link: string,
        subs: {
            sub?: string;
            sub2?: string;
            sub3?: string;
            sub4?: string;
            sub5?: string;
        },
    ) => {
        if (subs) {
            const subsTypeHack = subs as { [p: string]: string };
            let formattedLink = link;

            const subsList = getSubsArray(subsTypeHack);
            if (Array.isArray(subsList) && subsList.length > 0) {
                subsList.forEach(item => {
                    if (typeof item?.value === 'string' && item?.value.length > 0) {
                        formattedLink = modifyUrl(formattedLink, item.key, item.value);
                    }
                });
            }

            setCreative(state => ({ ...state, code: formattedLink }));
        }
    };

    const reduceLink = async creativeData => {
        if (reductionsNeed) {
            const resp = (await api.linkReduction.reduceLink({
                body: {
                    urlContainer: creativeData?.code,
                    domainCutter: reductionType,
                },
            })) as ICreateUseRequestInfiniteResponse<IGetLinkReductionData>;

            if (resp.data) {
                setCreative(state => ({ ...state, code: resp.data?.attributes[0]?.result }));
            } else
                notification.error({ message: t('An error occurred while shortening the link') });
        }
    };

    const onSubmitForm = useCallback(
        async (formData: IForm) => {
            setCreativeIsLoading(true);
            setFormError(null);
            // Вытаскиваю только нужные поля для креатива
            const preparedForm = prepareFormFields(formData, tTools('New link'), isAli);

            try {
                const requestFormData = new FormData();
                Object.keys(preparedForm).forEach(key => {
                    requestFormData.append(key, preparedForm[key]);
                });

                const response: IResponse<IGetCreativesData> = await api.creative.createCreative<IGetCreativesData>(
                    {
                        body: requestFormData,
                        isFormData: true,
                        headers: {
                            'X-Api-Version': '2.1',
                        },
                    },
                );

                if (response?.result) {
                    const preparedCreative = prepareCreativeData(response, formData);
                    setCreative(preparedCreative);
                    if (!isAli && !reductionsNeed)
                        addSubs(response.data.attributes.code, {
                            sub: formData.sub,
                            sub2: formData.sub2,
                            sub3: formData.sub3,
                            sub4: formData.sub4,
                            sub5: formData.sub5,
                        });
                    if (!isAli) await reduceLink(preparedCreative);
                    notification.success({ message: t('Creative create success') });
                    resetForm();
                    setCreativeIsLoading(false);
                }
                if (!response?.result && response.errors.length > 0) {
                    setFormError(response.errors);
                }

                return response;
            } catch (error) {
                return error;
            } finally {
                setCreativeIsLoading(false);
            }
        },
        [resetForm, t, tTools],
    );

    const showCopyInputs =
        !!creative.code?.length || !!creative?.erid.length || !!creative?.ordFullData.length;

    return (
        <div className="mb-36">
            <Head>
                <title>{tTools('Marking')}</title>
            </Head>
            <div className="mb-8">
                <h2 className="font-medium text-blueGray-600 text-sub xs:text-h2">
                    {tTools('Marking')}
                </h2>
                <p className="mt-4 text-p2 text-blueGray-500">
                    {t('Mark your advertising creatives before publication...')}{' '}
                    <br className="lg:hidden" />
                    <a
                        href="https://drive.google.com/file/d/13Lsux-gcJnLQzb-X8Wqg0dDe3Q8gNJyl/view?usp=sharing"
                        target="__blank"
                        className="underline text-blueGray-800"
                    >
                        {t('Instruction')}
                    </a>
                </p>
            </div>
            <Loader loading={creativeIsLoading}>
                <FormMarking
                    offersIsLoading={offersIsLoading}
                    placementsIsLoading={placementsIsLoading}
                    offers={offersData}
                    onChangeOffer={setOfferId}
                    placements={placementsData}
                    reductionList={reductionList}
                    reductionListLoading={reductionListLoading}
                    onChangeReduction={setReductionType}
                    reductionType={reductionType}
                    reductionsNeed={reductionsNeed}
                    setReductionsNeed={setReductionsNeed}
                    onSubmitForm={onSubmitForm}
                    formError={formError}
                    offerId={offerId}
                    isLoading={creativeIsLoading}
                    isAli={isAli}
                    form={form}
                    requiredPlacement={requiredPlacement}
                />
                {showCopyInputs && (
                    <div className="w-full p-6 mt-4 border border-t border-blueGray-200 rounded-[6px]">
                        <Grid gutter={[32, 24]}>
                            <Grid.Col xs={24} xl={24}>
                                <Loader loading={creativeIsLoading}>
                                    <BaseCopyableLink
                                        label={tTools('Your affiliate link')}
                                        text={creative.code}
                                    />
                                </Loader>
                            </Grid.Col>
                            <Grid.Col xs={24} md={creative?.aliArticle ? 12 : 24}>
                                <BaseCopyableLink label={t('Token')} text={creative?.erid} />
                            </Grid.Col>

                            {creative?.aliArticle && (
                                <Grid.Col xs={24} md={12}>
                                    <BaseCopyableLink
                                        label={tTools('Article')}
                                        text={creative?.aliArticle}
                                        description={tTools('Will work only for CIS audience')}
                                    />
                                </Grid.Col>
                            )}

                            <Grid.Col xs={24} xl={24}>
                                <BaseCopyableLink
                                    label={tTools('Advertiser')}
                                    text={creative?.ordFullData}
                                    description={
                                        isGlobalDomain
                                            ? 'This information is needed for advertising labeling.'
                                            : tTools(
                                                  'This information is needed for advertising labeling.',
                                              )
                                    }
                                />
                            </Grid.Col>
                        </Grid>
                    </div>
                )}
            </Loader>
        </div>
    );
};

export default MarkingPageForm;
